/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import currency from "currency.js";
import { format, parseISO } from "date-fns";

import { getTotalValue } from "@/app/lib/order-helpers";
import { CurrencyCode, OrderPosition, RegistryCode, TradePosition } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE } from "@/app/types/okta.types";

import { getDefaultEmptyString } from "../../pdf-components/documents/transaction-summary/order-table";
import {
  TransactionSummaryData,
  TransactionSummaryOrder,
  TransactionSummaryTransactionContract,
  TransactionSummaryTransactionContractProductAttributes,
} from "../../pdf-components/documents/transaction-summary/transaction-summary.types";
import { DATE_TIME_FORMAT } from "../../pdf-components/react-pdf-helpers";
import {
  compileHeadersAndDataForCSV,
  createEmptyRow,
  formatAmountWithCurrency,
  mapDataValuesToHeaderKeys,
} from "../csv-helpers";

type Headers =
  | "Order number"
  | "Order lodgement date"
  | "Type"
  | "Position"
  | "Quantity"
  | "Asset"
  | "Price"
  | "Eligibility"
  | "Certificate Type"
  | "Fuel Types"
  | "Technology Type"
  | "Location"
  | "Vintage"
  | "Certification schemes"
  | " "
  | "  ";

export const getOrderSpec = (
  order: TransactionSummaryOrder,
  rowRegistryCode: AggregatedRegistryCode,
  tradeType: "Spot" | "Forward" = "Spot",
): Partial<Record<Headers, string>> => {
  const isForwardTrade = tradeType === "Forward";
  const isActiveRegistryErcot = rowRegistryCode === RegistryCode.Ercot;
  const isActiveRegistryAny = rowRegistryCode === ANY_REGISTRY_CODE;

  const orderSpec: Partial<Record<Headers, string>> = {
    "Order number": order.orderNumber,
    "Order lodgement date": order.orderDate ? format(new Date(order.orderDate), DATE_TIME_FORMAT) : "",
    Type: order.type,
    Position: order.position,
    Quantity: order.volume?.toString() ?? "",
    Asset: order.assetType,
    Price: order.price ? formatAmountWithCurrency(order.price, order.currencyCode) : "0",
  };

  const ercotORanySpec: Partial<Record<Headers, string>> =
    isActiveRegistryErcot || isActiveRegistryAny
      ? {
          "Certificate Type":
            order.attributes?.certificateTypes?.join(", ") ||
            getDefaultEmptyString(order.position, { ignorePosition: isForwardTrade }),
        }
      : {};

  const mretsANDanySpec: Partial<Record<Headers, string>> = isActiveRegistryErcot
    ? {}
    : {
        Eligibility:
          order.attributes?.eligibilities?.join(", ") ||
          getDefaultEmptyString(order.position, { ignorePosition: isForwardTrade }),
        Location:
          order.attributes?.locations?.join(", ") ||
          getDefaultEmptyString(order.position, { ignorePosition: isForwardTrade }),
      };

  return {
    ...orderSpec,
    ...ercotORanySpec,
    ...mretsANDanySpec,
    [isActiveRegistryErcot ? "Technology Type" : "Fuel Types"]:
      order.attributes?.fuelSources?.join(", ") ||
      getDefaultEmptyString(order.position, { ignorePosition: isForwardTrade }),
    Vintage: `${
      order.attributes.vintages?.join(", ") || getDefaultEmptyString(order.position, { ignorePosition: isForwardTrade })
    }`,
    "Certification schemes":
      order.attributes.certifications?.join(", ") ||
      getDefaultEmptyString(order.position, { ignorePosition: isForwardTrade, bidDefaultEmptyString: "N/A" }),
  };
};

const getTransactionHeaders = (
  position: OrderPosition | TradePosition,
  feePercentage: number,
  rowRegistryCode: AggregatedRegistryCode,
): Partial<Record<Headers, string>> => {
  const isActiveRegistryErcot = rowRegistryCode === RegistryCode.Ercot;
  const isActiveRegistryMrets = rowRegistryCode === RegistryCode.Mrets;
  const isActiveRegistryAny = rowRegistryCode === ANY_REGISTRY_CODE;

  // The header matches here is not the bug or a mistake
  // The CSV format here is as follows
  //  order number  | Order lodgement date | Type                        |....
  //  202312312-ABC | 12:46 PM 01/29/2024  | LIMIT                       |....
  //
  //  Transactions  | Date of execution    | Application Tracking System |....
  //              1 | 3:27PM 01/31/2024    | Midwest Tracking System     |....
  //
  // The above CSV are kind of like merges of two CSV with multiple headers (download to get more clarity)
  // Hence converting JSON to CSV becomes a bit difficult
  // To manage this, order number is matched to Transactions, Order lodgement date is matched to Date of execution and so on
  // This ensures they are on the same column
  // Same goes for other values further added below.
  // the empty headers " " or "  " are the way to extend number of columns on the bottom if any since the transactions part can have lots of columns
  // We have helper functions to manage these in data as well, you can take a look at it

  // converting JSON to CSV works like
  // order number (key): 202312312-ABC (value)
  // but here transactions is also a value so
  // order number(key): transactions (value)
  // for them to be on the same column

  if (position === OrderPosition.Ask) {
    return {
      "Order number": `Transactions`,
      "Order lodgement date": "Date of execution",
      Type: "Application tracking system",
      Position: "Transaction Quantity",
      Quantity: "Executed price per unit",
      Asset: `Transaction Fee (${feePercentage}%)`,
      Price: "Total contract price (inclusive of transaction fee and applicable taxes)",
      ...(isActiveRegistryErcot && {
        "Certificate Type": "Certificate Type",
        "Technology Type": "Technology Type",
        Location: "Facility Identifier Number",
      }),
      ...(isActiveRegistryMrets && {
        Eligibility: "Eligibility(ies)",
        "Fuel Types": "Project location",
        Location: "Project name",
      }),
      ...(isActiveRegistryAny && {
        "Certificate Type": "Certificate Type",
        Eligibility: "Eligibility(ies)",
        Location: "Project location",
        "Fuel Types": "Project name",
      }),
      Vintage: "Vintage (mm-yyyy)",
      "Certification schemes": "Certification Schemes",
      " ": "Facility Effective Date",
      "  ": "Facility Expiry Date", //append more data with additional whitespace as keys
    };
  } else {
    return {
      "Order number": `Transactions`,
      "Order lodgement date": "Date of execution",
      Type: "Applicable tracking system",
      Position: "Transaction Quantity",
      Quantity: "Executed price per unit",
      Asset: `Transaction Fee (${feePercentage}%)`,
      Price: "Total contract price (inclusive of transaction fee and applicable taxes)",
      ...(isActiveRegistryErcot && {
        "Certificate Type": "Certificate Type",
        "Technology Type": "Technology Type",
      }),
      ...(isActiveRegistryMrets && { Eligibility: "Eligibility(ies)" }),
      ...(isActiveRegistryAny && {
        "Certificate Type": "Certificate Type",
        Eligibility: "Eligibility(ies)",
        "Fuel Types": "Fuel Types",
      }),
      Vintage: "Vintage",
      "Certification schemes": "Certification Schemes",
    };
  }
};

const getTransactionData = (
  transactionCount: number,
  orderPosition: OrderPosition | TradePosition,
  transaction: TransactionSummaryTransactionContract,
  productAttribute: TransactionSummaryTransactionContractProductAttributes,
  currencyCode: CurrencyCode,
  rowRegistryCode: AggregatedRegistryCode,
  tradeType: "Spot" | "Forward",
): Record<string, string> => {
  const isForwardTrade = tradeType === "Forward";
  const isActiveRegistryErcot = rowRegistryCode === RegistryCode.Ercot;
  const isActiveRegistryMrets = rowRegistryCode === RegistryCode.Mrets;
  const isActiveRegistryAny = rowRegistryCode === ANY_REGISTRY_CODE;

  const { transactionFeePercentage, position } = transaction;

  const executedPrice = formatAmountWithCurrency(transaction.price, currencyCode);
  const volume = (isForwardTrade ? productAttribute.volume : transaction.volume) ?? 0;

  const feeAmount = isForwardTrade
    ? currency(transaction.price).multiply(volume).multiply(transactionFeePercentage).divide(100).value
    : transaction.transactionFeeAmount;
  const transactionFee = formatAmountWithCurrency(feeAmount, currencyCode);

  const grossTotal = isForwardTrade
    ? getTotalValue(transaction.price, volume, orderPosition, transactionFeePercentage / 100).value
    : transaction.totalContractValue;

  const totalPrice = formatAmountWithCurrency(grossTotal, currencyCode);

  const defaultAttributeText = getDefaultEmptyString(position, { ignorePosition: isForwardTrade });

  if (position === OrderPosition.Ask) {
    return {
      transactionNumber: transactionCount.toString(),
      dateOfExecution: format(new Date(transaction.completedDate), DATE_TIME_FORMAT),
      trackingSystem: productAttribute.trackingSystem,
      transactionQuantity: volume.toString(),
      executedPrice,
      transactionFee,
      totalPrice,
      ...(isActiveRegistryErcot && {
        certificateTypes: productAttribute.certificateTypes?.join(", ") || defaultAttributeText,
        fuelSources: productAttribute.fuelSources?.join(", ") || defaultAttributeText,
      }),
      ...(isActiveRegistryMrets && {
        eligibilities: productAttribute.eligibilities?.join(", ") || defaultAttributeText,
        locations: productAttribute.locations?.join(", ") || defaultAttributeText,
      }),
      ...(isActiveRegistryAny && {
        certificateTypes: productAttribute.certificateTypes?.join(", ") || defaultAttributeText,
        eligibilities: productAttribute.eligibilities?.join(", ") || defaultAttributeText,
        locations: productAttribute.locations?.join(", ") || defaultAttributeText,
      }),
      project: productAttribute?.projects?.join(", ") || defaultAttributeText,
      vintage: productAttribute.vintages?.join(", ") || defaultAttributeText,
      certifications:
        productAttribute.certificationScheme?.certifications?.join(",") ||
        getDefaultEmptyString(position, { ignorePosition: isForwardTrade, bidDefaultEmptyString: "N/A" }),
      facilityEffectiveDate: productAttribute.certificationScheme?.crsEffectiveDate
        ? format(parseISO(productAttribute.certificationScheme?.crsEffectiveDate), "P")
        : "N/A",
      facilityExpiryDate: productAttribute.certificationScheme?.crsExpiryDate
        ? format(parseISO(productAttribute.certificationScheme?.crsExpiryDate), "P")
        : "N/A",
    };
  } else {
    return {
      transactionNumber: transactionCount.toString(),
      dateOfExecution: format(new Date(transaction.completedDate), DATE_TIME_FORMAT),
      trackingSystem: productAttribute.trackingSystem,
      transactionQuantity: volume.toString(),
      executedPrice,
      transactionFee,
      totalPrice,
      ...(isActiveRegistryErcot && {
        certificateTypes: productAttribute.certificateTypes?.join(", ") || defaultAttributeText,
        fuelSources: productAttribute.fuelSources?.join(", ") || defaultAttributeText,
      }),
      ...(isActiveRegistryMrets && {
        eligibilities: productAttribute.eligibilities?.join(",") || getDefaultEmptyString(position),
      }),
      ...(isActiveRegistryAny && {
        certificateTypes: productAttribute.certificateTypes?.join(", ") || defaultAttributeText,
        eligibilities: productAttribute.eligibilities?.join(", ") || defaultAttributeText,
        fuelSources: productAttribute.fuelSources?.join(", ") || defaultAttributeText,
      }),
      vintage: `${productAttribute.vintages?.join(",") || defaultAttributeText}`,
      certifications:
        productAttribute.certificationScheme?.certifications?.join(",") ||
        getDefaultEmptyString(position, { ignorePosition: isForwardTrade, bidDefaultEmptyString: "N/A" }),
    };
  }
};

export const getTransactionSummaryData = (
  orderPosition: OrderPosition | TradePosition,
  transactions: TransactionSummaryTransactionContract[],
  currencyCode: CurrencyCode,
  rowRegistryCode: AggregatedRegistryCode,
  tradeType: "Spot" | "Forward" = "Spot",
) => {
  const data: Record<string, string>[] = [];
  if (transactions?.length)
    data.push(
      // the fee amount is sure to have same percentage throughout the matched orders
      getTransactionHeaders(transactions[0].position, +transactions[0].transactionFeePercentage, rowRegistryCode),
    );

  let transactionsCount = 0;
  for (let i = 0; i < transactions.length; i++) {
    const transaction = transactions[i];
    for (let j = 0; j < transaction.productAttributes.length; j++) {
      const productAttribute = transaction.productAttributes[j];
      data.push(
        getTransactionData(
          ++transactionsCount,
          orderPosition,
          transaction,
          productAttribute,
          currencyCode,
          rowRegistryCode,
          tradeType,
        ),
      );
    }
  }
  return data;
};

export const createTransactionSummaryDataForCSV = ({
  transactionSummary,
  tradeType = "Spot",
  rowRegistryCode,
}: {
  tradeType: "Spot" | "Forward";
  rowRegistryCode: AggregatedRegistryCode;
  transactionSummary: TransactionSummaryData;
}) => {
  const orderSpec = getOrderSpec(transactionSummary.order, rowRegistryCode, tradeType);
  const transactions = getTransactionSummaryData(
    transactionSummary.order.position,
    transactionSummary.transactions,
    transactionSummary.order.currencyCode,
    rowRegistryCode,
    tradeType,
  );

  const modifiedTransactionData = transactions.map((transaction) => mapDataValuesToHeaderKeys(orderSpec, transaction));

  return compileHeadersAndDataForCSV([orderSpec, createEmptyRow(orderSpec)], modifiedTransactionData);
};
